import PropTypes from 'prop-types';
import { Component } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  CHANGE_PASSWORD_BUTTON,
  EMAIL_LABEL,
  FAVORITES_LABEL,
  GENERAL_SUBMIT_FAILED,
  GENERAL_SUBMIT_TEXT,
  NAME_LABEL,
  PASSWORD_LABEL,
  PRIVATE_PROFILE_LABEL,
  PUBLIC_PROFILE_LABEL,
  USERNAME_LABEL,
} from 'src/common/constants/localizations/settings';
import {
  EMAIL,
  EMAIL_ALREADY_EXISTS,
  EMAIL_REQUIRED,
  FULL_NAME_TOO_LONG,
  NAME,
  USERNAME,
  USERNAME_ALREADY_EXISTS,
  USERNAME_REQUIRED,
} from 'src/common/constants/localizations/shared';
import { changeUsername } from '../../../actions/auth';
import { updateUser } from '../../../actions/me';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import {
  composeValidators,
  isRequired,
  isValidEmail,
  isValidFullName,
} from '../../../utils/form/validation';
import PillButton from '../../shared/button/PillButton';
import FormInput from '../../shared/form/FormInput';
import { RadioButton } from '../../shared/form/RadioButton';
import TopLevelErrorContainer from '../../shared/form/TopLevelErrorContainer';
import commonSettingsCss from '../account-settings.module.scss';
import css from './general-settings.module.scss';

const fieldOrder = ['name', 'email', 'userName'];

const RadioButtonWrapper = ({ input = {}, ...rest }) => (
  <RadioButton
    {...input}
    defaultChecked={input.checked}
    handleClick={(evt) => {
      input.onChange(evt.target.value);
    }}
    spanClassName={css.radioButton}
    labelClassName={css.radioButtonLabel}
    {...rest}
  />
);

class GeneralSettingsForm extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      updateUser: PropTypes.func.isRequired,
      changeUsername: PropTypes.func.isRequired,
    }),
    handleChangePasswordClick: PropTypes.func.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  onSubmit = async ({ name, email, isFollowingListPublic, userName }) => {
    const { actions, onSubmitSuccess } = this.props;

    try {
      await actions.updateUser({
        name,
        email: email.trim?.(),
        isFollowingListPublic,
      });
      await this.handleUserNameChange(userName.trim());
      onSubmitSuccess?.();
    } catch (error) {
      const responseStatusText = error?.response?.statusText;

      // TODO/NOTE: we need to have a different validation response from platform to help drive
      // how an error is connected to a particular field (e.g. OPML validation responses).
      if (/email/i.test(responseStatusText)) {
        return { email: [[EMAIL_ALREADY_EXISTS, [email]]] };
      }
      if (/invalid registration name/i.test(responseStatusText)) {
        return {
          name: [FULL_NAME_TOO_LONG],
        };
      }

      // testing against errorResponse because the API response for this field value comes from
      // Account.ashx, not profiles/me
      if (/username/i.test(error)) {
        return {
          userName: [[USERNAME_ALREADY_EXISTS, [userName]]],
        };
      }
      return { base: [GENERAL_SUBMIT_FAILED] };
    }
  };

  handleUserNameChange = (newUserName) => {
    const { initialValues, actions } = this.props;

    if (initialValues.userName !== newUserName) {
      return actions.changeUsername(newUserName);
    }
  };

  render() {
    const { handleChangePasswordClick, initialValues } = this.props;

    const { getLocalizedText } = this.context;

    return (
      <Form initialValues={initialValues} onSubmit={this.onSubmit} keepDirtyOnReinitialize>
        {({
          handleSubmit,
          errors,
          // eslint-disable-next-line no-shadow
          hasValidationErrors,
          pristine,
          submitting,
          submitErrors,
          touched,
          modifiedSinceLastSubmit,
        }) => (
          <form name="generalSettings" data-testid="generalSettingsForm" onSubmit={handleSubmit}>
            {errors && (!pristine || touched) && (
              <div className={commonSettingsCss.largeSection}>
                <div className={commonSettingsCss.errorContainer}>
                  <TopLevelErrorContainer
                    errorMessages={{
                      ...errors,
                      ...(!modifiedSinceLastSubmit && submitErrors),
                    }}
                    errorTextClassName={css.formErrorMessageText}
                    fieldOrder={fieldOrder}
                    touchedFields={touched}
                  />
                </div>
              </div>
            )}
            <div className={commonSettingsCss.mediumSection}>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <p data-testid="nameLabel" className={css.label}>
                        {getLocalizedText(NAME_LABEL)}
                      </p>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        id="name"
                        component={FormInput}
                        hideInlineError
                        className={css.input}
                        dataTestId="name"
                        name="name"
                        placeholder={getLocalizedText(NAME)}
                        validate={composeValidators(isValidFullName)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <p data-testid="emailLabel" className={css.label}>
                        {getLocalizedText(EMAIL_LABEL)}
                      </p>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        id="email"
                        component={FormInput}
                        hideInlineError
                        className={css.input}
                        dataTestId="email"
                        name="email"
                        autoCorrect="off"
                        autoCapitalize="none"
                        placeholder={getLocalizedText(EMAIL)}
                        validate={composeValidators(isRequired(EMAIL_REQUIRED), isValidEmail)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <p data-testid="usernameLabel" className={css.label}>
                        {getLocalizedText(USERNAME_LABEL)}
                      </p>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        id="userName"
                        component={FormInput}
                        hideInlineError
                        className={css.input}
                        dataTestId="userName"
                        name="userName"
                        autoCorrect="off"
                        autoCapitalize="none"
                        placeholder={getLocalizedText(USERNAME)}
                        validate={composeValidators(isRequired(USERNAME_REQUIRED))}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.section}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <p data-testid="passwordLabel" className={css.label}>
                        {getLocalizedText(PASSWORD_LABEL)}
                      </p>
                    </div>
                    <div className="col-xs-12 col-sm-4">
                      <PillButton
                        data-testid="changePasswordButton"
                        id="changePasswordButton"
                        className={css.changePasswordButton}
                        type="button"
                        label={getLocalizedText(CHANGE_PASSWORD_BUTTON)}
                        onClick={handleChangePasswordClick}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={commonSettingsCss.largeSection}>
                <div className="row">
                  <div className={css.field}>
                    <div className="col-xs-12">
                      <p data-testid="favoritesLabel" className={css.label}>
                        {getLocalizedText(FAVORITES_LABEL)}
                      </p>
                    </div>
                    <div className="col-xs-12">
                      <Field
                        render={RadioButtonWrapper}
                        type="radio"
                        name="isFollowingListPublic"
                        dataTestId="privateProfileInd"
                        value="false"
                        label={getLocalizedText(PRIVATE_PROFILE_LABEL)}
                        className={css.leftRadioButton}
                      />
                      <Field
                        render={RadioButtonWrapper}
                        type="radio"
                        name="isFollowingListPublic"
                        dataTestId="publicProfileInd"
                        value="true"
                        label={getLocalizedText(PUBLIC_PROFILE_LABEL)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-4">
                  <PillButton
                    className={css.submitButtonGeneralSettings}
                    data-testid="generalSettingsSubmit"
                    id="generalSettingsSubmit"
                    type="submit"
                    label={getLocalizedText(GENERAL_SUBMIT_TEXT)}
                    isDisabled={submitting || hasValidationErrors || pristine}
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </Form>
    );
  }
}

function mapStateToProps(state) {
  const {
    me: { details },
  } = state;

  const initialValues = {
    name: details.title,
    userName: details.userName,
    email: details.email,
    // To handle cases where isFollowingListPublic does not have an associated boolean value
    isFollowingListPublic: details.isFollowingListPublic?.toString(),
  };

  return { initialValues };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateUser,
        changeUsername,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GeneralSettingsForm);
